import { uniq } from "lodash-es";
import moment from "moment";
import momentDurationFormatSetup from "moment-duration-format";
import { type FC, type ReactElement } from "react";

import { parseDuration } from "../../app-utils/chart-worker.js";
import { type IXAxisSettings, getEllipsedLabel } from "../../app-utils/chart.js";
import { type ChartLayoutState, type ColumnAnnotation } from "../../graphql.js";
import { DatasetChart, type IConditionCombination } from "./DatasetChart.js";
import { DURATION_UNIT } from "@/settings/settings.js";

// setup .format() for "moment"
momentDurationFormatSetup(moment);

export interface ICustomXAxisTickProps {
  data: IConditionCombination[]; // full data object with all combinations
  selectedAnnotations: ColumnAnnotation[]; // all selected annotations
  layoutSettings: ChartLayoutState; // all layout settings
  axisSettings: IXAxisSettings; // x=axis settings in order to calculate positions of labels

  x?: number; // [INJECTED] x position of the tick
  y?: number; // [INJECTED] y position of the tick
  index?: number; // [INJECTED] index of the tick
  width?: number; // [INJECTED] width of the chart
  payload?: any; // [INJECTED] object containing the actual tick value as prepared by Recharts library
}

function formatDuration(d, formatString: string) {
  const duration = moment.duration(d, "seconds");
  return duration.format(formatString, { trim: false });
}

function getDurationFormatString(annotation: ColumnAnnotation) {
  return uniq(annotation.valueSet.map((value: string) => parseDuration(value).unit))
    .map((unit: string) => {
      switch (unit) {
        case DURATION_UNIT.SECONDS:
          return "s [sec]";
        case DURATION_UNIT.MINUTES:
          return "m [min]";
        case DURATION_UNIT.HOURS:
          return "h [hrs]";
        case DURATION_UNIT.DAYS:
          return "d [days]";
        case DURATION_UNIT.WEEKS:
          return "w [weeks]";
        case DURATION_UNIT.MONTHS:
          return "M [months]";
        case DURATION_UNIT.YEARS:
          return "y [years]";
      }
    })
    .reverse()
    .join(" ");
}

/**
 * <description>
 *
 * created on 3/15/2017
 */
const CustomXAxisTick: FC<ICustomXAxisTickProps> = (props) => {
  const groupWidth = (groupSize: number): number => groupSize * bucketWidth + (groupSize - 1) * bucketPadding;

  const renderGroupIndicator = (innerGroupSize: number, yPosition: number): ReactElement => {
    const x1: number = -(bucketWidth / 2);
    const x2: number = x1 + groupWidth(innerGroupSize);

    const p1 = `${x1},${yPosition - 27}`;
    const p2 = `${x1},${yPosition - 20}`;
    const p3 = `${x2},${yPosition - 20}`;
    const p4 = `${x2},${yPosition - 27}`;

    return (
      <polyline
        className="variant-connector"
        points={`${p1} ${p2} ${p3} ${p4}`}
        stroke="#d8d8d8"
        strokeWidth={1}
        fill="none"
      />
    );
  };

  const { x, y, index, axisSettings, payload, data, layoutSettings, selectedAnnotations } = props;
  const {
    bucketWidth,
    bucketPadding,
    firstLabelRotated,
    firstLabelYOffset,
    firstLabelHeight,
    secondLabelYOffset,
    secondLabelRotated,
    secondLabelHeight,
    thirdLabelYOffset,
    thirdLabelRotated,
  } = axisSettings;

  const comb: IConditionCombination = data[index];
  // If it's a time series we might not have the same number of ticks
  // In this case, we will use the payload.value for the tick label
  if (!comb && !layoutSettings.timeSeries) {
    return null;
  }
  const showSecondLabel: boolean = comb && comb.isFirstLeadingInner && selectedAnnotations.length > 1;
  const showThirdLabel: boolean = comb && comb.isSecondLeadingInner && selectedAnnotations.length > 2;

  let firstValue: string = null;
  let firstLabel: string = null;
  if (layoutSettings.timeSeries) {
    const durationFormatString = getDurationFormatString(selectedAnnotations[0]);
    // apply ticks as a duration, then we have to format these
    if (durationFormatString && durationFormatString.trim() !== "") {
      firstLabel = formatDuration(payload.value, durationFormatString);
    } else {
      // else this is a numeric type, we just take the provided value from the domain automatically calculated by Recharts
      firstLabel = payload.value;
    }
    firstValue = payload.value;
  } else {
    firstValue = comb.conditions[0].value;
    firstLabel = getEllipsedLabel(firstValue);
  }
  let secondLevelGroupIndicator: ReactElement = null;
  let secondLabelXOffset = 0;
  const secondLabelYPosition: number = firstLabelYOffset + firstLabelHeight + secondLabelYOffset;

  if (showSecondLabel) {
    // move the label of the second condition to the right depending on how many different values the first condition has
    secondLabelXOffset = groupWidth(comb.firstInnerGroupSize) / 2 - bucketWidth / 2;
    secondLevelGroupIndicator = renderGroupIndicator(comb.firstInnerGroupSize, secondLabelYPosition);
  }

  let thirdLevelGroupIndicator: ReactElement = null;
  let thirdLabelXOffset = 0;
  const thirdLabelYPosition: number =
    firstLabelYOffset + firstLabelHeight + secondLabelYOffset + secondLabelHeight + thirdLabelYOffset;

  if (showThirdLabel) {
    // move the label of the third condition to the right depending on how many different values the second condition has
    thirdLabelXOffset = groupWidth(comb.secondInnerGroupSize) / 2 - bucketWidth / 2;
    thirdLevelGroupIndicator = renderGroupIndicator(comb.secondInnerGroupSize, thirdLabelYPosition);
  }

  // Hide x-axis labels if there is not enough space to even show the rotated labels without collisions
  if (!layoutSettings.timeSeries && bucketWidth < DatasetChart.MIN_BUCKET_WIDTH) {
    return null;
  }

  return (
    <g transform={`translate(${x},${y})`}>
      <text
        x={0}
        y={firstLabelYOffset}
        transform={!layoutSettings.timeSeries && firstLabelRotated ? "rotate(-35)" : ""}
        textAnchor={!layoutSettings.timeSeries && firstLabelRotated ? "end" : "middle"}
        fill="#666"
      >
        {firstLabel}
        <title>{firstValue}</title>
      </text>
      {showSecondLabel && (
        <g transform={`translate(${secondLabelXOffset},${secondLabelYPosition})`}>
          <text
            x={0}
            y={0}
            transform={secondLabelRotated ? "rotate(-35)" : ""}
            textAnchor={secondLabelRotated ? "end" : "middle"}
            fill="#666"
          >
            {getEllipsedLabel(comb.conditions[1]?.value)}
            <title>{comb.conditions[1]?.value}</title>
          </text>
        </g>
      )}
      {secondLevelGroupIndicator}
      {showThirdLabel && (
        <g transform={`translate(${thirdLabelXOffset},${thirdLabelYPosition})`}>
          <text
            x={0}
            y={0}
            transform={thirdLabelRotated ? "rotate(-35)" : ""}
            textAnchor={thirdLabelRotated ? "end" : "middle"}
            fill="#666"
          >
            {getEllipsedLabel(comb.conditions[2]?.value)}
            <title>{comb.conditions[2]?.value}</title>
          </text>
        </g>
      )}
      {thirdLevelGroupIndicator}
    </g>
  );
};

export default CustomXAxisTick;
